import { useEffect, useMemo, useState } from "react";
import { useLayout } from "./context";
import { PublicLayout } from "./PublicLayout";
import { Col, Loader, Row } from "taltech-styleguide";
import { Section } from "./BaseLayout";
import { MainAppViewStore } from "../../configuration/MainAppViewStore";
import { useInject, useWindowSize } from "../../hooks";
import { ModalProvider } from "@components-common/modals";
import { useScreen } from "@components-system";
import { useUserContext } from "../../_runtime/iam/session.context";
import { Error403Screen } from "@components-common/errors";
import { WorkerProvider } from "../../workers/worker-provider";

/**
 * Functional Component: WorkspaceLayout
 *
 * This component renders the layout for the workspace, including the sidebar, content area, and header.
 * It uses various hooks for context, layout management, user information, and window size.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {JSX.Element[]} [props.children] - The children elements to be rendered inside the layout.
 * @param {any} props.menuItems - The menu items to be rendered in the sidebar.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the workspace layout.
 */
export const WorkspaceLayout = (
  props: any & { children?: JSX.Element[]; menuItems: any }
) => {
  const { setCurrentLayoutId } = useLayout();
  const screenProps = useScreen();
  const { role } = useUserContext();
  const mainAppViewStore = useInject<MainAppViewStore>("main-view-store");
  const [open, setOpen] = useState<boolean>(true);
  const [isHeaderSmaller, setIsHeaderSmaller] = useState<boolean>(false);
  const { width } = useWindowSize();
  const [headerClasses, setHeaderClasses] = useState<string>("");
  const headerClassNames = useMemo(() => headerClasses, [headerClasses]);
  useEffect(() => {
    setCurrentLayoutId("layout_workspace");
  }, [setCurrentLayoutId]);
  useEffect(() => {
    if (!headerClassNames.includes("tt-desktop-header--scrolled")) {
      setIsHeaderSmaller(false);
    } else {
      setIsHeaderSmaller(true);
    }
  }, [headerClassNames]);

  const header = document.getElementsByClassName("edb-header")[0];

  if (header) {
    const mutationCallback = (mutationsList: any) => {
      for (let mutation of mutationsList) {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          setHeaderClasses(mutation.target.className);
        }
      }
    };

    const observer = new MutationObserver(mutationCallback);

    const config = {
      attributes: true,
      attributeFilter: ["class"],
    };

    observer.observe(header, config);
  }

  const loader = mainAppViewStore.isLoading ? <Loader overlay /> : <></>;
  setCurrentLayoutId("layout.workspace");

  const { route, ...rest } = props;

  return (
    <PublicLayout>
      <WorkerProvider>
        <ModalProvider>
          <div className="pt-3 container-fluid">
            {loader}
            <Row
              className={
                width >= 1200 ? (open ? "ebd-row-open" : "ebd-row-closed") : ""
              }
            >
              <Col
                className={`p-0 sidebar ${isHeaderSmaller && "smaller-header"}`}
              >
                <div className="ebd-nav-bar-left-bg">
                  <Section id="sidebar" />
                </div>
              </Col>
              <Col className={"ebd-col-width-open"}>
                {/**
                 * Conditional Rendering in Content Area:
                 * - If `role` and `screenProps` are available, it checks if the user's role is permitted for the current route.
                 * - If the user's role is included in the route's roles, it renders the `Section` component with ID "content".
                 * - If the user's role is not permitted, it renders the `Error403Screen` component.
                 * - If `role` or `screenProps` are not available, it renders a full-screen `Loader` component.
                 */}
                {role && screenProps ? (
                  <>
                    {screenProps?.route.roles.includes(role) ? (
                      <Section id="content" />
                    ) : (
                      <Error403Screen />
                    )}
                  </>
                ) : (
                  <Loader fullScreen />
                )}
              </Col>
            </Row>
          </div>
        </ModalProvider>
      </WorkerProvider>
      {/* <div className={"ebd-no-margin"}>{children}</div> */}
    </PublicLayout>
  );
};
